// Skeleton loader components for content placeholders

export function SkeletonLine({ width = '100%', height = '1rem', className = '' }) {
  return (
    <div
      className={`rounded animate-shimmer ${className}`}
      style={{ width, height }}
    />
  );
}

export function SkeletonCircle({ size = '2.5rem', className = '' }) {
  return (
    <div
      className={`rounded-full animate-shimmer ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

export function SkeletonCard({ className = '' }) {
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 shadow-sm border border-gray-100 dark:border-gray-700 ${className}`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <SkeletonCircle size="2.5rem" />
        <div className="flex-1 space-y-2">
          <SkeletonLine width="60%" height="1rem" />
          <SkeletonLine width="40%" height="0.75rem" />
        </div>
        <SkeletonCircle size="2rem" />
      </div>
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex gap-2">
        <SkeletonLine width="4rem" height="1.5rem" className="rounded-full" />
        <SkeletonLine width="4rem" height="1.5rem" className="rounded-full" />
        <SkeletonLine width="4rem" height="1.5rem" className="rounded-full" />
      </div>
    </div>
  );
}

export function SkeletonModuleCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl mb-3 sm:mb-4 shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden animate-fade-in">
      <div className="p-3 sm:p-5">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2.5 sm:gap-4 flex-1">
            <SkeletonCircle size="2.5rem" className="flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <SkeletonLine width="70%" height="1rem" />
              <SkeletonLine width="50%" height="0.75rem" />
            </div>
          </div>
          <SkeletonCircle size="2rem" className="flex-shrink-0" />
        </div>
        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          <SkeletonLine width="5rem" height="1.5rem" className="rounded-full" />
          <SkeletonLine width="5rem" height="1.5rem" className="rounded-full" />
          <SkeletonLine width="5rem" height="1.5rem" className="rounded-full" />
        </div>
      </div>
    </div>
  );
}

export function SkeletonCourseCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 animate-fade-in">
      <div className="flex items-start gap-3">
        <SkeletonCircle size="1.25rem" />
        <SkeletonCircle size="2.5rem" />
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <SkeletonLine width="60%" height="1rem" />
            <SkeletonLine width="3rem" height="1.25rem" className="rounded-full" />
          </div>
          <SkeletonLine width="80%" height="0.75rem" />
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex gap-4">
              <SkeletonLine width="5rem" height="0.75rem" />
              <SkeletonLine width="5rem" height="0.75rem" />
            </div>
            <SkeletonLine width="6rem" height="1rem" className="mt-2" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function SkeletonModuleList({ count = 5 }) {
  return (
    <div className="space-y-3 sm:space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonModuleCard key={i} />
      ))}
    </div>
  );
}

export function SkeletonCourseList({ count = 3 }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCourseCard key={i} />
      ))}
    </div>
  );
}

// Research page card skeleton - matches ResearchCourseCard collapsed state
export function SkeletonResearchCard() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl mb-3 shadow-sm overflow-hidden">
      <div className="p-3 sm:p-5">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <div className="flex items-center gap-2.5 sm:gap-4 min-w-0 flex-1">
            {/* Icon circle */}
            <SkeletonCircle size="2.5rem" className="shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 sm:gap-2">
                {/* Title */}
                <SkeletonLine width="60%" height="1rem" />
                {/* Badge */}
                <SkeletonLine width="4rem" height="1.25rem" className="rounded-full shrink-0" />
              </div>
            </div>
          </div>
          {/* Expand button */}
          <SkeletonCircle size="2rem" className="shrink-0" />
        </div>
      </div>
    </div>
  );
}

// Research page skeleton - matches the Research page layout
export function SkeletonResearchPage() {
  return (
    <div className="space-y-6">
      {/* Research section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <SkeletonCircle size="0.5rem" />
          <SkeletonLine width="5rem" height="0.875rem" />
        </div>
        <SkeletonResearchCard />
        <SkeletonResearchCard />
      </div>
      {/* Competition section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <SkeletonCircle size="0.5rem" />
          <SkeletonLine width="6rem" height="0.875rem" />
        </div>
        <SkeletonResearchCard />
      </div>
    </div>
  );
}

// Planner course item skeleton - matches CourseCheckboxItem
export function SkeletonPlannerCourseItem() {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-transparent">
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <SkeletonLine width="1.25rem" height="1.25rem" className="rounded mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {/* Course icon */}
            <SkeletonCircle size="1rem" className="shrink-0" />
            {/* Course title */}
            <SkeletonLine width="70%" height="0.875rem" />
          </div>
          {/* Course details */}
          <SkeletonLine width="50%" height="0.75rem" className="mt-2" />
          {/* Price */}
          <SkeletonLine width="5rem" height="0.875rem" className="mt-2" />
        </div>
      </div>
    </div>
  );
}

// Planner page skeleton - matches the Planner page layout
export function SkeletonPlannerPage() {
  return (
    <div>
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <SkeletonLine width="12rem" height="1.5rem" />
        <SkeletonLine width="16rem" height="0.875rem" className="mt-2" />
      </div>

      {/* Semester filter */}
      <div className="flex gap-2 mb-4">
        <SkeletonLine width="6rem" height="2.25rem" className="rounded-full" />
        <SkeletonLine width="6rem" height="2.25rem" className="rounded-full" />
        <SkeletonLine width="5rem" height="2.25rem" className="rounded-full" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 border-b border-gray-200 dark:border-gray-700 mb-4 pb-2">
        <SkeletonLine width="7rem" height="1rem" />
        <SkeletonLine width="5rem" height="1rem" />
      </div>

      {/* Course items */}
      <div className="space-y-3">
        <SkeletonPlannerCourseItem />
        <SkeletonPlannerCourseItem />
        <SkeletonPlannerCourseItem />
      </div>
    </div>
  );
}

export function SkeletonHeader() {
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <SkeletonLine width="8rem" height="2rem" />
        <SkeletonCircle size="2rem" />
      </div>
      <SkeletonLine width="100%" height="2.5rem" className="rounded-lg" />
      <SkeletonLine width="100%" height="2.5rem" className="rounded-lg" />
    </div>
  );
}

export function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonLine
          key={i}
          width={i === lines - 1 ? '60%' : '100%'}
          height="0.875rem"
        />
      ))}
    </div>
  );
}

export function SkeletonAvatar({ size = 'md' }) {
  const sizes = {
    sm: '2rem',
    md: '2.5rem',
    lg: '3rem',
    xl: '4rem',
  };
  return <SkeletonCircle size={sizes[size] || sizes.md} />;
}

export function SkeletonButton({ width = '6rem', height = '2.5rem' }) {
  return <SkeletonLine width={width} height={height} className="rounded-lg" />;
}

export function SkeletonTable({ rows = 5, cols = 4 }) {
  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex gap-4 pb-2 border-b border-gray-200 dark:border-gray-700">
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonLine key={i} width={`${100 / cols}%`} height="1rem" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4 py-2">
          {Array.from({ length: cols }).map((_, colIndex) => (
            <SkeletonLine key={colIndex} width={`${100 / cols}%`} height="0.875rem" />
          ))}
        </div>
      ))}
    </div>
  );
}

// Default export for backwards compatibility
export default function Skeleton({ variant = 'line', ...props }) {
  switch (variant) {
    case 'circle':
      return <SkeletonCircle {...props} />;
    case 'card':
      return <SkeletonCard {...props} />;
    case 'module':
      return <SkeletonModuleCard {...props} />;
    case 'course':
      return <SkeletonCourseCard {...props} />;
    case 'text':
      return <SkeletonText {...props} />;
    case 'avatar':
      return <SkeletonAvatar {...props} />;
    case 'button':
      return <SkeletonButton {...props} />;
    default:
      return <SkeletonLine {...props} />;
  }
}
